/*
 *
 *
 *
 *   Copyright (c) 2022. TinyMedic Pvt Ltd  - All Rights Reserved.
 *   This file is part of the API module  of the project: TinyMedic-Platform.
 *
 *
 *   Proprietary and confidential.
 *
 *   Unauthorized copying of this file, via any medium is strictly prohibited.
 *    Any resources of   TinyMedic-Platform can not be copied and/or distributed without the express permission of TinyMedic Pvt Ltd.
 *
 *
 *
 */

import React, { useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Button, Col, FormText, Row } from 'reactstrap';
import { Translate, ValidatedField, ValidatedForm, isNumber, translate } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities as getChronicConditions } from 'app/entities/chronic-condition/chronic-condition.reducer';
import { getEntities as getDoctors } from 'app/entities/doctor/doctor.reducer';
import { getEntities as getGlucoUsers } from 'app/entities/gluco-user/gluco-user.reducer';
import { ClinicalStatus } from 'app/shared/model/enumerations/clinical-status.model';
import { VerificationStatus } from 'app/shared/model/enumerations/verification-status.model';
import { ConditionCategory } from 'app/shared/model/enumerations/condition-category.model';
import { ConditionSeverity } from 'app/shared/model/enumerations/condition-severity.model';
import { createEntity, getEntity, reset, updateEntity } from './patient-condition.reducer';

export const PatientConditionUpdate = () => {
  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const { id } = useParams<'id'>();
  const isNew = id === undefined;

  const chronicConditions = useAppSelector(state => state.chronicCondition.entities);
  const doctors = useAppSelector(state => state.doctor.entities);
  const glucoUsers = useAppSelector(state => state.glucoUser.entities);
  const patientConditionEntity = useAppSelector(state => state.patientCondition.entity);
  const loading = useAppSelector(state => state.patientCondition.loading);
  const updating = useAppSelector(state => state.patientCondition.updating);
  const updateSuccess = useAppSelector(state => state.patientCondition.updateSuccess);
  const clinicalStatusValues = Object.keys(ClinicalStatus);
  const verificationStatusValues = Object.keys(VerificationStatus);
  const conditionCategoryValues = Object.keys(ConditionCategory);
  const conditionSeverityValues = Object.keys(ConditionSeverity);

  const handleClose = () => {
    navigate(`/patient-condition${location.search}`);
  };

  useEffect(() => {
    if (isNew) {
      dispatch(reset());
    } else {
      dispatch(getEntity(id));
    }

    dispatch(getChronicConditions({}));
    dispatch(getDoctors({}));
    dispatch(getGlucoUsers({}));
  }, []);

  useEffect(() => {
    if (updateSuccess) {
      handleClose();
    }
  }, [updateSuccess]);

  const saveEntity = values => {
    if (values.microSec !== undefined && typeof values.microSec !== 'number') {
      values.microSec = Number(values.microSec);
    }
    if (values.lastupdated !== undefined && typeof values.lastupdated !== 'number') {
      values.lastupdated = Number(values.lastupdated);
    }
    if (values.timestampentry !== undefined && typeof values.timestampentry !== 'number') {
      values.timestampentry = Number(values.timestampentry);
    }
    if (values.cursor !== undefined && typeof values.cursor !== 'number') {
      values.cursor = Number(values.cursor);
    }
    if (values.startDate !== undefined && typeof values.startDate !== 'number') {
      values.startDate = Number(values.startDate);
    }
    if (values.suspendedDate !== undefined && typeof values.suspendedDate !== 'number') {
      values.suspendedDate = Number(values.suspendedDate);
    }

    const entity = {
      ...patientConditionEntity,
      ...values,
      chronicCondition: chronicConditions.find(it => it.id.toString() === values.chronicCondition?.toString()),
      suspendedBy: doctors.find(it => it.id.toString() === values.suspendedBy?.toString()),
      reportedBy: doctors.find(it => it.id.toString() === values.reportedBy?.toString()),
      patient: glucoUsers.find(it => it.id.toString() === values.patient?.toString()),
    };

    if (isNew) {
      dispatch(createEntity(entity));
    } else {
      dispatch(updateEntity(entity));
    }
  };

  const defaultValues = () =>
    isNew
      ? {}
      : {
          clinicalStatus: 'ACTIVE',
          verificationStatus: 'UNCONFIRMED',
          conditionCategory: 'PROBLEM_LIST_ITEM',
          conditionSeverity: 'SCT_24484000',
          ...patientConditionEntity,
          chronicCondition: patientConditionEntity?.chronicCondition?.id,
          suspendedBy: patientConditionEntity?.suspendedBy?.id,
          reportedBy: patientConditionEntity?.reportedBy?.id,
          patient: patientConditionEntity?.patient?.id,
        };

  return (
    <div>
      <Row className="justify-content-center">
        <Col md="8">
          <h2 id="tinyMedicRestApp.patientCondition.home.createOrEditLabel" data-cy="PatientConditionCreateUpdateHeading">
            <Translate contentKey="tinyMedicRestApp.patientCondition.home.createOrEditLabel">Create or edit a PatientCondition</Translate>
          </h2>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col md="8">
          {loading ? (
            <p>Loading...</p>
          ) : (
            <ValidatedForm defaultValues={defaultValues()} onSubmit={saveEntity}>
              {!isNew ? (
                <ValidatedField
                  name="id"
                  required
                  readOnly
                  id="patient-condition-id"
                  label={translate('tinyMedicRestApp.patientCondition.id')}
                  validate={{ required: true }}
                />
              ) : null}
              <ValidatedField
                label={translate('tinyMedicRestApp.patientCondition.active')}
                id="patient-condition-active"
                name="active"
                data-cy="active"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.patientCondition.microSec')}
                id="patient-condition-microSec"
                name="microSec"
                data-cy="microSec"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.patientCondition.lastupdated')}
                id="patient-condition-lastupdated"
                name="lastupdated"
                data-cy="lastupdated"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.patientCondition.timestampentry')}
                id="patient-condition-timestampentry"
                name="timestampentry"
                data-cy="timestampentry"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.patientCondition.cursor')}
                id="patient-condition-cursor"
                name="cursor"
                data-cy="cursor"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.patientCondition.startDate')}
                id="patient-condition-startDate"
                name="startDate"
                data-cy="startDate"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  validate: v => isNumber(v) || translate('entity.validation.number'),
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.patientCondition.startObservations')}
                id="patient-condition-startObservations"
                name="startObservations"
                data-cy="startObservations"
                type="text"
                validate={{
                  maxLength: { value: 500, message: translate('entity.validation.maxlength', { max: 500 }) },
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.patientCondition.suspendedDate')}
                id="patient-condition-suspendedDate"
                name="suspendedDate"
                data-cy="suspendedDate"
                type="text"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.patientCondition.suspendedObservations')}
                id="patient-condition-suspendedObservations"
                name="suspendedObservations"
                data-cy="suspendedObservations"
                type="text"
                validate={{
                  maxLength: { value: 500, message: translate('entity.validation.maxlength', { max: 500 }) },
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.patientCondition.suspended')}
                id="patient-condition-suspended"
                name="suspended"
                data-cy="suspended"
                check
                type="checkbox"
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.patientCondition.clinicalStatus')}
                id="patient-condition-clinicalStatus"
                name="clinicalStatus"
                data-cy="clinicalStatus"
                type="select"
              >
                {clinicalStatusValues.map(clinicalStatus => (
                  <option value={clinicalStatus} key={clinicalStatus}>
                    {translate(`tinyMedicRestApp.ClinicalStatus.${clinicalStatus}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.patientCondition.verificationStatus')}
                id="patient-condition-verificationStatus"
                name="verificationStatus"
                data-cy="verificationStatus"
                type="select"
              >
                {verificationStatusValues.map(verificationStatus => (
                  <option value={verificationStatus} key={verificationStatus}>
                    {translate(`tinyMedicRestApp.VerificationStatus.${verificationStatus}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.patientCondition.conditionCategory')}
                id="patient-condition-conditionCategory"
                name="conditionCategory"
                data-cy="conditionCategory"
                type="select"
              >
                {conditionCategoryValues.map(conditionCategory => (
                  <option value={conditionCategory} key={conditionCategory}>
                    {translate(`tinyMedicRestApp.ConditionCategory.${conditionCategory}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.patientCondition.conditionSeverity')}
                id="patient-condition-conditionSeverity"
                name="conditionSeverity"
                data-cy="conditionSeverity"
                type="select"
              >
                {conditionSeverityValues.map(conditionSeverity => (
                  <option value={conditionSeverity} key={conditionSeverity}>
                    {translate(`tinyMedicRestApp.ConditionSeverity.${conditionSeverity}`)}
                  </option>
                ))}
              </ValidatedField>
              <ValidatedField
                label={translate('tinyMedicRestApp.patientCondition.snomedBodySiteConceptId')}
                id="patient-condition-snomedBodySiteConceptId"
                name="snomedBodySiteConceptId"
                data-cy="snomedBodySiteConceptId"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  maxLength: { value: 50, message: translate('entity.validation.maxlength', { max: 50 }) },
                }}
              />
              <ValidatedField
                label={translate('tinyMedicRestApp.patientCondition.snomedBodySiteConceptText')}
                id="patient-condition-snomedBodySiteConceptText"
                name="snomedBodySiteConceptText"
                data-cy="snomedBodySiteConceptText"
                type="text"
                validate={{
                  required: { value: true, message: translate('entity.validation.required') },
                  maxLength: { value: 500, message: translate('entity.validation.maxlength', { max: 500 }) },
                }}
              />
              <ValidatedField
                id="patient-condition-chronicCondition"
                name="chronicCondition"
                data-cy="chronicCondition"
                label={translate('tinyMedicRestApp.patientCondition.chronicCondition')}
                type="select"
                required
              >
                <option value="" key="0" />
                {chronicConditions
                  ? chronicConditions.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <FormText>
                <Translate contentKey="entity.validation.required">This field is required.</Translate>
              </FormText>
              <ValidatedField
                id="patient-condition-suspendedBy"
                name="suspendedBy"
                data-cy="suspendedBy"
                label={translate('tinyMedicRestApp.patientCondition.suspendedBy')}
                type="select"
              >
                <option value="" key="0" />
                {doctors
                  ? doctors.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="patient-condition-reportedBy"
                name="reportedBy"
                data-cy="reportedBy"
                label={translate('tinyMedicRestApp.patientCondition.reportedBy')}
                type="select"
              >
                <option value="" key="0" />
                {doctors
                  ? doctors.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <ValidatedField
                id="patient-condition-patient"
                name="patient"
                data-cy="patient"
                label={translate('tinyMedicRestApp.patientCondition.patient')}
                type="select"
                required
              >
                <option value="" key="0" />
                {glucoUsers
                  ? glucoUsers.map(otherEntity => (
                      <option value={otherEntity.id} key={otherEntity.id}>
                        {otherEntity.id}
                      </option>
                    ))
                  : null}
              </ValidatedField>
              <FormText>
                <Translate contentKey="entity.validation.required">This field is required.</Translate>
              </FormText>
              <Button tag={Link} id="cancel-save" data-cy="entityCreateCancelButton" to="/patient-condition" replace color="info">
                <FontAwesomeIcon icon="arrow-left" />
                &nbsp;
                <span className="d-none d-md-inline">
                  <Translate contentKey="entity.action.back">Back</Translate>
                </span>
              </Button>
              &nbsp;
              <Button color="primary" id="save-entity" data-cy="entityCreateSaveButton" type="submit" disabled={updating}>
                <FontAwesomeIcon icon="save" />
                &nbsp;
                <Translate contentKey="entity.action.save">Save</Translate>
              </Button>
            </ValidatedForm>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default PatientConditionUpdate;
